import React, { useState } from "react";
import { Box, Typography, Paper, Button } from "@mui/material";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import { BORDER, SEVERITIES, TEXT_PRIMARY, TEXT_SECONDARY } from "../constants";
import { CHANGE_LABELS } from "./SectionComparison";
import { FilterChip } from "./CorrectionsList";

const severityOf = (key) => SEVERITIES.find((s) => s.key === key);

const InlineChange = ({ finding }) => {
  if (finding.type === "move") {
    return (
      <Typography dir="auto" sx={{ fontSize: 15, color: TEXT_PRIMARY }}>
        <bdi>{finding.after}</bdi>{" "}
        <Box component="span" sx={{ color: TEXT_SECONDARY }}>
          ממקום {finding.beforePosition} למקום {finding.afterPosition}
        </Box>
      </Typography>
    );
  }
  return (
    <Typography component="div" sx={{ fontSize: 15, lineHeight: 1.9, color: TEXT_SECONDARY }}>
      {finding.contextBefore && <>…{finding.contextBefore} </>}
      {finding.before && (
        <Box component="span" sx={{ bgcolor: "#fee2e2", color: "#991b1b", textDecoration: "line-through", px: "3px", borderRadius: "3px" }}>
          <bdi>{finding.before}</bdi>
        </Box>
      )}
      {finding.before && finding.after && " "}
      {finding.after && (
        <Box component="span" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 600, px: "3px", borderRadius: "3px" }}>
          <bdi>{finding.after}</bdi>
        </Box>
      )}
      {finding.contextAfter && <> {finding.contextAfter}…</>}
    </Typography>
  );
};

/** Changes no required correction accounts for (spec 3.4, 3.6) */
const UnrequestedList = ({ check, findingsById, onShowFindings }) => {
  const [severity, setSeverity] = useState("all");
  const control = check.correctionsControl;

  if (check.status === "controlling") {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: `1px dashed ${BORDER}`, textAlign: "center" }}>
        <Typography color={TEXT_SECONDARY}>הרשימה תוצג כשבקרת התיקונים תסתיים</Typography>
      </Paper>
    );
  }

  const findings = (control?.unrequestedFindingIds || []).map((id) => findingsById.get(id)).filter(Boolean);
  if (!findings.length) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: `1px solid ${BORDER}`, textAlign: "center" }}>
        <TaskAltRoundedIcon sx={{ fontSize: 36, color: "#22c55e", mb: 1 }} />
        <Typography fontWeight={700} color={TEXT_PRIMARY}>
          לא נמצאו שינויים שלא נדרשו
        </Typography>
        <Typography variant="body2" color={TEXT_SECONDARY} sx={{ mt: 0.5 }}>
          כל השינויים בגרסה המעודכנת מוסברים ברשימת התיקונים.
        </Typography>
      </Paper>
    );
  }

  const counts = Object.fromEntries(SEVERITIES.map(({ key }) => [key, findings.filter((f) => f.severity === key).length]));
  const visible = findings.filter((f) => severity === "all" || f.severity === severity);

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 1.5 }}>
        {control?.state === "skipped"
          ? "לא הוזנה רשימת תיקונים, לכן כל השינויים בין הגרסאות מוצגים כאן."
          : "שינויים שנמצאו בגרסה המעודכנת ולא הותאמו לאף תיקון ברשימה — ייתכן שנכנסו בטעות בסבב התיקונים."}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
        <FilterChip label="הכל" count={findings.length} active={severity === "all"} onClick={() => setSeverity("all")} />
        {SEVERITIES.filter(({ key }) => counts[key] > 0).map(({ key, label, color }) => (
          <FilterChip key={key} label={label} count={counts[key]} color={color} active={severity === key} onClick={() => setSeverity(key)} />
        ))}
      </Box>
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
        {visible.map((finding) => {
          const config = severityOf(finding.severity);
          return (
            <Box
              key={finding.id}
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                px: { xs: 2, sm: 2.5 },
                py: 1.75,
                "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` },
              }}
            >
              <Box
                component="span"
                sx={{ flexShrink: 0, mt: "3px", px: 0.9, py: 0.2, borderRadius: 1, bgcolor: config?.bg, color: config?.color, fontSize: 13, fontWeight: 700 }}
              >
                {config?.label}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontWeight: 600 }}>
                  {finding.sectionLabel} · {CHANGE_LABELS[finding.type] || finding.type}
                </Typography>
                <InlineChange finding={finding} />
              </Box>
              <Button
                size="small"
                onClick={() => onShowFindings([finding.id])}
                aria-label="הצגה בהשוואה"
                sx={{ minWidth: 0, flexShrink: 0, color: TEXT_SECONDARY }}
              >
                <CompareArrowsRoundedIcon fontSize="small" />
              </Button>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default UnrequestedList;
